import * as Apollo from "@apollo/client"

// Thanks much to ChatGPT.

/**
 * useMutationWithDefaults
 *
 * A wrapper around Apollo's `useMutation` that makes mutation variables flexible.
 *
 * Normally, Apollo requires you to supply *all* required variables every time you
 * call the mutation, even if some were already given when initializing the hook.
 *
 * This utility relaxes that restriction:
 *   - You can pass variables at hook initialization (`baseOptions.variables`).
 *   - You can pass variables at execution time (`mutateFn({ variables })`).
 *   - You can split required variables between the two — they’ll be merged automatically (Apollo does this by default).
 *
 * Developer Experience:
 *   - TS no longer complains about missing required variables if you only pass a subset.
 *   - IntelliSense still shows you all valid variable fields.
 *   - Apollo always receives the merged set of variables under the hood.
 *
 * Example:
 * ```ts
 * const [updateCohortStatus, result] = useMutationWithDefaults(
 *   useUpdateCohortStatusMutation,
 *   { variables: { id: 1 } } // supply one required variable here
 * )
 *
 * // later, provide the rest — no need to repeat `id`
 * updateCohortStatus({ variables: { status: GroupStatus.InProgress } })
 * ```
 *
 * At runtime, Apollo sees:
 * {
 *   id: 1,
 *   status: "InProgress"
 * }
 */

type RequiredKeys<T> = {
  [K in keyof T]-?: {} extends Pick<T, K> ? never : K
}[keyof T]

type MergeMissing<TVars, D extends Partial<TVars>> = Partial<TVars> &
  Pick<TVars, Exclude<RequiredKeys<TVars>, keyof D>>

export function useMutationWithDefaults<
  TData,
  TVars extends Record<string, any>,
  D extends Partial<TVars> = {}
>(
  useHook: (
    baseOptions?: Apollo.MutationHookOptions<TData, TVars>
  ) => [Apollo.MutationFunction<TData, TVars>, Apollo.MutationResult<TData>],
  baseOptions?: Omit<Apollo.MutationHookOptions<TData, TVars>, "variables"> & {
    variables?: D
  }
) {
  const defaults = (baseOptions?.variables ?? {}) as D

  const [mutate, result] = useHook({
    ...baseOptions,
    variables: defaults as unknown as TVars | undefined
  })

  type VarsForCall = MergeMissing<TVars, D>
  type OptionsForCall = Omit<
    Apollo.MutationFunctionOptions<TData, TVars>,
    "variables"
  > & {
    variables: VarsForCall
  }

  // Overloads: if VarsForCall still has required keys, force options;
  // otherwise allow options to be omitted.
  function wrappedMutate(options: OptionsForCall): ReturnType<typeof mutate>
  function wrappedMutate(
    options?: Omit<
      Apollo.MutationFunctionOptions<TData, TVars>,
      "variables"
    > & {
      variables?: VarsForCall
    }
  ): ReturnType<typeof mutate> {
    return mutate({
      ...options,
      variables: options?.variables as TVars
    })
  }

  return [wrappedMutate, result] as const
}
